/**
 * Shared modal shell: Escape-to-close, click-outside-to-close, body
 * scroll-lock, backdrop, and the standard card chrome used by every
 * admin dialog. Replaces the ~30-line ref/useEffect/className
 * boilerplate that each dialog was copy-pasting.
 *
 * Nesting: a module-level stack tracks which dialog is on top. Only
 * the topmost dialog responds to Escape + backdrop clicks, so an
 * archive/delete confirm inside BookingDetailDialog dismisses itself
 * without also dismissing the parent.
 */
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

// Stack of active dialogs — only the top handles Escape. Using a module-
// level ref instead of React context so a child ModalDialog nested
// several components deep Just Works without providers.
const dialogStack: Array<() => void> = []

type MaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'

const MAX_WIDTH_CLASS: Record<MaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
}

export interface ModalDialogProps {
  onClose: () => void
  children: React.ReactNode
  /** Header row rendered inside the shell; close button is appended automatically. */
  header?: React.ReactNode
  maxWidth?: MaxWidth
  /** When false, clicking the backdrop won't close — useful for confirm modals. */
  closeOnBackdrop?: boolean
  className?: string
}

export function ModalDialog({
  onClose,
  children,
  header,
  maxWidth = '2xl',
  closeOnBackdrop = true,
  className,
}: ModalDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogStack.push(onClose)
    function handleEscape(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Only the topmost dialog handles Escape. Everyone else no-ops so
      // a nested confirm can dismiss itself without also closing its
      // parent.
      if (dialogStack[dialogStack.length - 1] !== onClose) return
      onClose()
    }
    function handleClickOutside(e: MouseEvent) {
      if (!closeOnBackdrop) return
      if (dialogStack[dialogStack.length - 1] !== onClose) return
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
      const idx = dialogStack.lastIndexOf(onClose)
      if (idx !== -1) dialogStack.splice(idx, 1)
      // Only restore scroll when the outermost dialog unmounts.
      if (dialogStack.length === 0) {
        document.body.style.overflow = prevOverflow
      }
    }
  }, [onClose, closeOnBackdrop])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        ref={dialogRef}
        className={`bg-white dark:bg-stone-900 rounded-2xl shadow-2xl w-full ${MAX_WIDTH_CLASS[maxWidth]} max-h-[90vh] overflow-hidden flex flex-col ${className ?? ''}`}
      >
        {header !== undefined && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 dark:border-stone-800">
            <div className="flex items-center gap-3 min-w-0">{header}</div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
